/**
 * Dex crop capture + storage handoff — Push 3 / v1.4.3 b3.
 *
 * The DOM/iOS glue for requirement (F): take a frame data URL + a
 * fractional cell rectangle and produce a high-resolution rounded-rectangle
 * crop for a REGISTERED cell, then persist it under a region+dex key.
 *
 * Honest storage stance (the user asked us not to fake saved files):
 *   - The live iOS broadcast path delivers frames as data URLs in the
 *     WKWebView; there is no app-writable `/assets/` directory exposed to
 *     JS at runtime. So `/assets/<region>_<dex>.png` is the LOGICAL path we
 *     emit in the JSON contract, while the ACTUAL bytes are persisted as a
 *     data URL in localStorage (mirrored to Capacitor Preferences), exactly
 *     like the existing `localSpriteStore`. `resolveCropDataUrl()` reads the
 *     bytes back by the logical path.
 *   - Every save reports its real outcome (`persisted`, `quota_exceeded`,
 *     `no_dom`, …). We never claim a crop was saved when it was not — the
 *     session diagnostics surface `cropsUnavailable` so a tester sees the
 *     truth.
 *
 * Pure parts (path keying, decision) live in `dexImagePipeline.ts`; this
 * file is the side-effecting layer and is intentionally thin.
 */

import {
  cropAssetPath,
  cropAssetFilename,
  type DexRegionTag,
  type DexCellRect,
} from "./dexImagePipeline";
import { CANVAS_READBACK } from "./canvasReadback";

const KEY_PREFIX = "gx.dexcrop.v1.";
const INDEX_KEY = "gx.dexcrop.v1.__index";
const DEFAULT_LRU_CAP = 320;

export const DEX_CROP_EVENT = "gx:dex-crop-updated";

export type DexCropPersistResult =
  | "persisted"
  | "already_stored"
  | "no_dom"
  | "no_path"
  | "crop_failed"
  | "quota_exceeded";

export interface DexCropCaptureResult {
  /** Logical asset path emitted in the session JSON, or null on failure. */
  assetPath: string | null;
  /** Did we actually persist bytes for this path? */
  persisted: boolean;
  /** Outcome detail for diagnostics. */
  result: DexCropPersistResult;
  /** Byte length of the stored data URL, when persisted. */
  bytes?: number;
}

interface CropIndexEntry {
  key: string;
  path: string;
  savedAt: number;
  bytes: number;
}

interface CropIndex {
  entries: CropIndexEntry[];
}

// ── storage shims (mirror localSpriteStore) ─────────────────────────────────

function readLocal(key: string): string | null {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: string): boolean {
  try {
    if (typeof window === "undefined") return false;
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function removeLocal(key: string): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

async function mirrorToPrefs(key: string, value: string): Promise<void> {
  try {
    const mod = await import("@capacitor/preferences" as any);
    const Preferences = mod?.Preferences;
    if (!Preferences) return;
    await Preferences.set({ key, value });
  } catch {
    /* not a Capacitor runtime, or too large — localStorage is source of truth. */
  }
}

function readIndex(): CropIndex {
  const raw = readLocal(INDEX_KEY);
  if (!raw) return { entries: [] };
  try {
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.entries)) return { entries: parsed.entries };
  } catch {
    /* malformed — reset */
  }
  return { entries: [] };
}

function writeIndex(idx: CropIndex): void {
  writeLocal(INDEX_KEY, JSON.stringify(idx));
}

/** Pure: oldest-first keys to evict so the index stays under `cap`. */
export function pickCropEvictionKeys(idx: CropIndex, cap: number): string[] {
  if (cap <= 0) return idx.entries.map((e) => e.key);
  if (idx.entries.length <= cap) return [];
  const sorted = [...idx.entries].sort((a, b) => a.savedAt - b.savedAt);
  return sorted.slice(0, sorted.length - cap).map((e) => e.key);
}

function enforceCap(idx: CropIndex, cap: number): CropIndex {
  const evictKeys = pickCropEvictionKeys(idx, cap);
  if (evictKeys.length === 0) return idx;
  const evict = new Set(evictKeys);
  for (const k of evictKeys) removeLocal(KEY_PREFIX + k);
  return { entries: idx.entries.filter((e) => !evict.has(e.key)) };
}

// ── canvas crop ─────────────────────────────────────────────────────────────

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("dex_crop_image_decode_failed"));
    img.src = dataUrl;
  });
}

/**
 * Crop the exact rounded-rectangle cell from the frame at native screenshot
 * resolution, trimming the whitespace gutter between grid items so the
 * asset is tight to the card. Returns a PNG data URL (lossless — preserves
 * the sprite colours the registration classifier relied on).
 *
 * `insetPct` shrinks the rect slightly on each side to drop the inter-cell
 * whitespace; the rounded corners are approximated with a clip path.
 */
export async function cropCellToDataUrl(
  frameDataUrl: string,
  rect: DexCellRect,
  insetPct = 0.06,
): Promise<string | null> {
  if (typeof document === "undefined" || typeof Image === "undefined") return null;
  try {
    const img = await loadImage(frameDataUrl);
    const fw = img.naturalWidth || img.width;
    const fh = img.naturalHeight || img.height;
    if (!fw || !fh) return null;

    const insetX = rect.wPct * insetPct;
    const insetY = rect.hPct * insetPct;
    const sx = Math.round((rect.xPct + insetX) * fw);
    const sy = Math.round((rect.yPct + insetY) * fh);
    const sw = Math.max(1, Math.round((rect.wPct - 2 * insetX) * fw));
    const sh = Math.max(1, Math.round((rect.hPct - 2 * insetY) * fh));

    const canvas = document.createElement("canvas");
    canvas.width = sw;
    canvas.height = sh;
    const ctx = canvas.getContext("2d", CANVAS_READBACK);
    if (!ctx) return null;

    // Rounded-rect clip so the saved asset matches the in-game cell shape
    // and we don't carry the square-corner background gutter.
    const radius = Math.min(sw, sh) * 0.16;
    roundedRectPath(ctx, 0, 0, sw, sh, radius);
    ctx.clip();
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
    return canvas.toDataURL("image/png");
  } catch {
    return null;
  }
}

function roundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

// ── public capture API ──────────────────────────────────────────────────────

export interface CaptureRegisteredCropInput {
  frameDataUrl: string;
  region: DexRegionTag;
  dexNumber: number | string;
  rect: DexCellRect;
  lruCap?: number;
}

/**
 * Capture + persist a crop for a REGISTERED cell. The caller is responsible
 * for gating on `shouldCaptureCrop` (registered + stable + near-middle)
 * before calling — this function does the crop + store and reports the
 * honest outcome.
 *
 * Idempotent per logical path: a second call for the same region+dex within
 * a session is a no-op (`already_stored`).
 */
export async function captureRegisteredCrop(
  input: CaptureRegisteredCropInput,
): Promise<DexCropCaptureResult> {
  const path = cropAssetPath(input.region, input.dexNumber);
  const filename = cropAssetFilename(input.region, input.dexNumber);
  if (!path || !filename) {
    return { assetPath: null, persisted: false, result: "no_path" };
  }
  if (typeof window === "undefined" || typeof document === "undefined") {
    // No DOM (SSR / Node tests): we still hand back the logical path so the
    // JSON contract is complete, but we are honest that nothing was stored.
    return { assetPath: path, persisted: false, result: "no_dom" };
  }

  const storageKey = KEY_PREFIX + filename;
  const idx = readIndex();
  if (idx.entries.some((e) => e.key === filename) && readLocal(storageKey)) {
    return { assetPath: path, persisted: true, result: "already_stored" };
  }

  const dataUrl = await cropCellToDataUrl(input.frameDataUrl, input.rect);
  if (!dataUrl) {
    return { assetPath: path, persisted: false, result: "crop_failed" };
  }

  let ok = writeLocal(storageKey, dataUrl);
  if (!ok) {
    // Evict one and retry once (iOS QuotaExceededError after long sessions).
    const trimmed = enforceCap(idx, Math.max(1, (input.lruCap ?? DEFAULT_LRU_CAP) - 1));
    writeIndex(trimmed);
    ok = writeLocal(storageKey, dataUrl);
    if (!ok) {
      return { assetPath: path, persisted: false, result: "quota_exceeded" };
    }
  }

  const updated: CropIndex = {
    entries: [
      ...idx.entries.filter((e) => e.key !== filename),
      { key: filename, path, savedAt: Date.now(), bytes: dataUrl.length },
    ],
  };
  writeIndex(enforceCap(updated, input.lruCap ?? DEFAULT_LRU_CAP));
  void mirrorToPrefs(storageKey, dataUrl);
  try {
    window.dispatchEvent(new CustomEvent(DEX_CROP_EVENT, { detail: { path } }));
  } catch {
    /* non-DOM */
  }
  return { assetPath: path, persisted: true, result: "persisted", bytes: dataUrl.length };
}

/** Read back the stored crop bytes (data URL) for a logical asset path. */
export function resolveCropDataUrl(assetPath: string): string | null {
  // /assets/kanto_0125.png -> kanto_0125.png
  const filename = assetPath.split("/").pop();
  if (!filename) return null;
  return readLocal(KEY_PREFIX + filename);
}

/** Lightweight diagnostics: how many crops are stored + total bytes. */
export function dexCropStoreStats(): { count: number; bytes: number } {
  const idx = readIndex();
  return {
    count: idx.entries.length,
    bytes: idx.entries.reduce((s, e) => s + (e.bytes || 0), 0),
  };
}

/** Drop every stored Dex crop (privacy control + tests). */
export function clearAllDexCrops(): number {
  const idx = readIndex();
  for (const e of idx.entries) removeLocal(KEY_PREFIX + e.key);
  writeIndex({ entries: [] });
  return idx.entries.length;
}
